import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'wouter';
import { BookOpen, Plus, Flag, MapPin } from 'lucide-react';
import type { ClubAPI } from '@shared/types/api';
import { useAdminClubs, useCreateClub } from '../api/useAdminClubs';
import { useToast } from '@/components/ux/Toast';
import { Loader } from '@/components/Loader';

const AUDIENCE_LABEL: Record<ClubAPI.ClubAudience, string> = {
  sapphic: 'Sapphic',
  gay: 'Gay',
  everyone: 'Everyone',
};

const STATUS_CLASS: Record<ClubAPI.ClubStatus, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  draft: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400',
  unopened: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  archived: 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500',
};

function slugify(name: string) {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
}

export default function AdminClubs() {
  const [, navigate] = useLocation();
  const toast = useToast();
  const { data: clubs = [], isLoading } = useAdminClubs();
  const createClub = useCreateClub();

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [audience, setAudience] = useState<ClubAPI.ClubAudience>('sapphic');

  const handleCreate = () => {
    if (!name.trim()) return;
    createClub.mutate(
      { name: name.trim(), slug: slugify(name), audience, status: 'draft' },
      {
        onSuccess: (club) => {
          toast.success('Club created');
          navigate(`/clubs/${club.id}`);
        },
        onError: (err: any) => toast.error(err?.message || 'Failed to create club'),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-primary-500" />
          <h1 className="text-2xl font-bold dark:text-neutral-100">Clubs</h1>
        </div>
        <button
          onClick={() => setCreating((v) => !v)}
          className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors"
        >
          <Plus className="w-4 h-4" /> New Club
        </button>
      </div>

      <p className="text-sm text-dark-muted dark:text-neutral-400 mb-6">
        Clubs replace the Discover tab for users in the <code>clubs_tab</code> experiment. Members only see clubs for
        their audience. A city is <em>ready</em> once it reaches the club's member threshold and has no upcoming club event.
      </p>

      {creating && (
        <div className="card p-4 mb-6 flex flex-wrap items-end gap-3">
          <label className="flex-1 min-w-[200px]">
            <span className="block text-xs font-medium text-dark-muted mb-1">Name</span>
            <input
              className="input input-bordered w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sapphic Book Club"
            />
          </label>
          <label>
            <span className="block text-xs font-medium text-dark-muted mb-1">Audience</span>
            <select
              className="select select-bordered"
              value={audience}
              onChange={(e) => setAudience(e.target.value as ClubAPI.ClubAudience)}
            >
              {Object.entries(AUDIENCE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || createClub.isPending}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-50"
          >
            Create
          </button>
        </div>
      )}

      {clubs.length === 0 ? (
        <div className="card p-12 text-center">
          <BookOpen className="w-12 h-12 text-dark-muted dark:text-neutral-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold dark:text-neutral-100 mb-2">No clubs yet</h3>
          <p className="text-dark-muted dark:text-neutral-400">Create your first club to get started.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-neutral-700 text-left text-sm text-dark-muted dark:text-neutral-400">
                <th className="py-3 px-4 font-medium">Name</th>
                <th className="py-3 px-4 font-medium">Audience</th>
                <th className="py-3 px-4 font-medium">Status</th>
                <th className="py-3 px-4 font-medium">Members</th>
                <th className="py-3 px-4 font-medium">Ready cities</th>
                <th className="py-3 px-4 font-medium">Reports</th>
              </tr>
            </thead>
            <tbody>
              {clubs.map((club) => (
                <tr
                  key={club.id}
                  onClick={() => navigate(`/clubs/${club.id}`)}
                  className="border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 cursor-pointer text-sm"
                >
                  <td className="py-3 px-4">
                    <div className="font-medium dark:text-neutral-100">{club.name}</div>
                    <div className="text-xs text-dark-muted">/{club.slug}</div>
                  </td>
                  <td className="py-3 px-4">{AUDIENCE_LABEL[club.audience]}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASS[club.status]}`}>
                      {club.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">{club.memberCount}</td>
                  <td className="py-3 px-4">
                    {club.readyCities > 0 ? (
                      <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                        <MapPin className="w-3.5 h-3.5" /> {club.readyCities}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="py-3 px-4">
                    {club.pendingReports > 0 ? (
                      <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                        <Flag className="w-3.5 h-3.5" /> {club.pendingReports}
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}
